import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";
import { api, apiErr } from "@/lib/api";
import { CATEGORIES, LAYOUTS, themeOf } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ArrowRight, Sparkles, FileText, LayoutTemplate, Upload, Loader2, X } from "lucide-react";

const BEGIN = [
  { id: "scratch", label: "Start from Scratch", desc: "A blank canvas with starter events.", icon: FileText },
  { id: "template", label: "Use a Template", desc: "Start from a polished, ready-made design.", icon: LayoutTemplate },
  { id: "ai", label: "Generate with AI", desc: "Describe a topic and let AI build a draft.", icon: Sparkles },
  { id: "import", label: "Import Data", desc: "Bring in a CSV, JSON, or text file.", icon: Upload },
];

const stepVariants = {
  enter: { opacity: 0, y: 24 }, center: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -24 },
};

const LayoutPreview = ({ id }) => {
  const t = themeOf("aurora");
  return (
    <div className="h-28 rounded-xl relative overflow-hidden mb-4" style={{ background: t.bgGradient }}>
      <div className="absolute inset-0 p-4">
        {id === "vertical" && <div className="relative pl-4 h-full"><div className="absolute left-0 top-0 bottom-0 w-[2px]" style={{ background: t.line }} />{[0,1,2].map(i=><div key={i} className="flex items-center gap-2 mb-2"><span className="h-2 w-2 rounded-full -ml-[3px]" style={{background:t.dot}} /><span className="h-1.5 rounded-full" style={{width:70-i*10,background:`${t.accent}aa`}} /></div>)}</div>}
        {id === "horizontal" && <div className="flex items-center h-full"><div className="w-full h-[2px]" style={{background:t.line}} /></div>}
        {id === "cards" && <div className="grid grid-cols-3 gap-2 h-full">{[0,1,2].map(i=><div key={i} className="rounded" style={{background:`${t.surface}`}} />)}</div>}
        {id === "fullscreen" && <div className="h-full rounded-lg" style={{background:t.surface}} />}
        {id === "zigzag" && <svg className="w-full h-full"><polyline points="10,70 55,25 100,70 145,25 190,70" fill="none" stroke={t.accent} strokeWidth="2"/></svg>}
      </div>
    </div>
  );
};

export default function CreateWizard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(params.get("mode") === "ai" ? 3 : 0);
  const [category, setCategory] = useState("");
  const [begin, setBegin] = useState("");
  const [layout, setLayout] = useState("vertical");
  const [aiPrompt, setAiPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const back = () => setStep((s) => Math.max(0, s - 1));

  const chooseBegin = (id) => {
    setBegin(id);
    if (id === "template") { nav("/templates"); return; }
    if (id === "import") { nav("/import"); return; }
    if (id === "ai") { setStep(3); return; }
    setStep(2);
  };

  const createScratch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/timelines", { title: title || "Untitled Timeline", category: category || "Custom", layout });
      toast.success("Timeline created");
      nav(`/editor/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const createAI = async () => {
    if (aiPrompt.trim().length < 8) { toast.error("Describe your timeline in a bit more detail"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate", { prompt: aiPrompt, category: category || "Custom" });
      toast.success("Draft generated");
      nav(`/editor/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="h-16 flex items-center justify-between px-5 sm:px-8 border-b border-border glass sticky top-0 z-40">
        <Logo />
        <Link to="/dashboard"><Button variant="ghost" size="icon" data-testid="wizard-close"><X size={20} /></Button></Link>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-12 sm:py-20">
        <div className="flex items-center gap-2 mb-10">
          {[0,1,2].map((i) => <div key={i} className={`h-1.5 rounded-full transition-all ${(step >= i && step < 3) || (step === 3 && i === 0) ? "bg-primary w-10" : "bg-muted w-6"}`} />)}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Step 1</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">What are you creating?</h1>
              <p className="mt-2 text-muted-foreground">Pick the closest match. You can change this later.</p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CATEGORIES.map((c) => (
                  <button key={c} data-testid={`cat-${c}`} onClick={() => { setCategory(c); setStep(1); }}
                    className={`text-left rounded-2xl border p-4 text-sm font-medium transition-all hover:-translate-y-0.5 ${category === c ? "border-primary bg-accent text-primary" : "border-border bg-card text-foreground/80 hover:border-primary/40"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
              <button onClick={back} className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground"><ArrowLeft size={15} /> Back</button>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Step 2</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">How would you like to begin?</h1>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {BEGIN.map(({ id, label, desc, icon: Icon }) => (
                  <button key={id} data-testid={`begin-${id}`} onClick={() => chooseBegin(id)}
                    className="text-left rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary/40">
                    <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent text-primary mb-4"><Icon size={20} /></div>
                    <div className="font-display text-lg font-semibold text-foreground">{label}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
              <button onClick={back} className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground"><ArrowLeft size={15} /> Back</button>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Step 3</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Choose a layout</h1>
              <p className="mt-2 text-muted-foreground">Every layout is switchable later in the editor.</p>
              <div className="mt-6">
                <Input placeholder="Give your timeline a title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 mb-6" data-testid="wizard-title" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {LAYOUTS.map((l) => (
                  <button key={l.id} data-testid={`layout-${l.id}`} onClick={() => setLayout(l.id)}
                    className={`text-left rounded-2xl border p-4 transition-all hover:-translate-y-1 ${layout === l.id ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40"} bg-card`}>
                    <LayoutPreview id={l.id} />
                    <div className="font-display font-semibold text-foreground">{l.name}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">{l.desc}</p>
                  </button>
                ))}
              </div>
              <Button onClick={createScratch} disabled={busy} className="mt-8 rounded-full h-12 px-8" data-testid="wizard-finish">
                {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : null} Open Editor <ArrowRight size={18} className="ml-1" />
              </Button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
              <button onClick={() => setStep(params.get("mode") === "ai" ? 0 : 1)} className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground"><ArrowLeft size={15} /> Back</button>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-primary mb-3"><Sparkles size={14} /> Create with AI</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Describe your timeline</h1>
              <p className="mt-2 text-muted-foreground">AI will propose a title, sections, dates and events as an editable draft. Facts should be verified before publishing.</p>
              <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="ai-prompt"
                placeholder="e.g. The history of artificial intelligence from Alan Turing to modern generative AI"
                className="mt-6 min-h-[140px] text-base" />
              <div className="mt-3 flex flex-wrap gap-2">
                {["History of the Roman Empire", "Evolution of the smartphone", "Journey to Mars", "The life of Leonardo da Vinci"].map((s) => (
                  <button key={s} onClick={() => setAiPrompt(s)} className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">{s}</button>
                ))}
              </div>
              <Button onClick={createAI} disabled={busy} className="mt-8 rounded-full h-12 px-8" data-testid="ai-generate-btn">
                {busy ? <><Loader2 className="animate-spin mr-2" size={18} /> Generating…</> : <>Generate Timeline <Sparkles size={16} className="ml-2" /></>}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
